// ./src/components/features/PollQuestions/PollQuestionEditDrawer/PollOptionsEditor.tsx
//
// Inline editor for a poll/quiz question's answer choices. Controlled: the parent owns
// the drafts array (order = display order). Like the RSVP question editor, it keeps a
// single trailing empty row and auto-appends a new one as soon as the last gets a value
// — so there's no explicit "Add" button. When the question is a quiz, a "correct" toggle
// appears (single-choice enforces exactly-one, multi-choice allows many). Empty rows are
// dropped on save by the drawer.

import React, { useCallback } from "react";
import { Stack, Group, TextInput, Checkbox, ActionIcon, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import { POLL_QUESTION_TYPE } from "@/utils/api";

export interface PollOptionDraft {
  id: string;
  label: string;
  isCorrect: boolean;
}

const genId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `opt-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
};

export const makeOption = (label = "", isCorrect = false): PollOptionDraft => ({
  id: genId(),
  label,
  isCorrect,
});

/**
 * Keep exactly one trailing empty row: append a fresh empty option when the last one has
 * a label, and seed a single empty row when the list is empty. Editing the last row's
 * label is therefore what spawns the next one (no explicit "Add" button).
 */
export const withTrailingEmptyOptions = (options: PollOptionDraft[]): PollOptionDraft[] => {
  const last = options[options.length - 1];

  if (options.length === 0) {
    return [makeOption()];
  }

  return last && last.label.trim() !== "" ? [...options, makeOption()] : options;
};

export interface PollOptionsEditorProps {
  value: PollOptionDraft[];
  onChange: (value: PollOptionDraft[]) => void;
  /** When true, show the correct-answer toggle. */
  isQuiz: boolean;
  /** single | multi — drives single-correct (radio-like) vs multi-correct behaviour. */
  questionType: string;
  error?: string;
}

export const PollOptionsEditor: React.FC<PollOptionsEditorProps> = ({
  value,
  onChange,
  isQuiz,
  questionType,
  error,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const isSingle = questionType === POLL_QUESTION_TYPE.SINGLE;

  const updateLabel = useCallback(
    (id: string, label: string) => {
      onChange(withTrailingEmptyOptions(value.map((o) => (o.id === id ? { ...o, label } : o))));
    },
    [value, onChange]
  );

  const toggleCorrect = useCallback(
    (id: string, checked: boolean) => {
      // Single-choice quiz: exactly one correct answer, so selecting one clears the rest.
      if (isSingle && checked) {
        onChange(value.map((o) => ({ ...o, isCorrect: o.id === id })));

        return;
      }

      onChange(value.map((o) => (o.id === id ? { ...o, isCorrect: checked } : o)));
    },
    [value, onChange, isSingle]
  );

  const removeOption = useCallback(
    (id: string) => {
      onChange(withTrailingEmptyOptions(value.filter((o) => o.id !== id)));
    },
    [value, onChange]
  );

  return (
    <Stack gap="xs">
      {value.map((option, index) => {
        // The single trailing empty row is a placeholder for the next option — it has no
        // "correct" toggle and no remove button (there's nothing to remove yet).
        const isTrailingEmpty = index === value.length - 1 && option.label.trim() === "";

        return (
          <Group key={option.id} gap="xs" wrap="nowrap" align="center">
            {isQuiz ? (
              isTrailingEmpty ? (
                <span style={{ width: 20, flexShrink: 0 }} />
              ) : (
                <Checkbox
                  checked={option.isCorrect}
                  onChange={(e) => toggleCorrect(option.id, e.currentTarget.checked)}
                  radius={isSingle ? "xl" : "sm"}
                  aria-label={t("events:pollQuestions.form.options.correct", { defaultValue: "Mark as correct answer" })}
                  title={t("events:pollQuestions.form.options.correct", { defaultValue: "Mark as correct answer" })}
                />
              )
            ) : null}

            <TextInput
              style={{ flex: 1 }}
              placeholder={t("events:pollQuestions.form.options.placeholder", {
                defaultValue: "Option {{n}}",
                n: index + 1,
              })}
              value={option.label}
              onChange={(e) => updateLabel(option.id, e.currentTarget.value)}
              size="md"
            />

            {isTrailingEmpty ? (
              <span style={{ width: 28, flexShrink: 0 }} />
            ) : (
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => removeOption(option.id)}
                aria-label={t("common:remove", { defaultValue: "Remove" })}
                title={t("common:remove", { defaultValue: "Remove" })}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            )}
          </Group>
        );
      })}

      {error ? (
        <Text size="sm" c="red">
          {error}
        </Text>
      ) : null}

      {isQuiz ? (
        <Text size="sm" c="dimmed">
          {isSingle
            ? t("events:pollQuestions.form.options.hintSingle", { defaultValue: "Tick the one correct answer." })
            : t("events:pollQuestions.form.options.hintMulti", { defaultValue: "Tick every correct answer." })}
        </Text>
      ) : null}
    </Stack>
  );
};
